import { useState } from 'react';
import styled, { css } from 'styled-components';

import ValidationCheck from '@/components/atom/icon/ValidationCheck';
import BottomSheet from '@/components/molecules/BottomSheet';
import theme from '@/styles/theme';
import { Client } from '@/types/app';

interface Props {
  client: Client;
  onCloseWithTodayInvisible: (isNotTodayOpen: boolean) => void;
}
const CommunityCloseNotice = ({ client, onCloseWithTodayInvisible }: Props) => {
  const [todayInvisible, setTodayInvisible] = useState(false);

  if (client.isWebView) {
    return (
      <MobileWrapper>
        <OutOfServiceSvg />
        <MobileHeader>커뮤니티 서비스 중단 안내</MobileHeader>
        <MobileContentScrollWrapper>
          <Content>{`안녕하세요. 닥터나우 입니다.\n먼저 커뮤니티 서비스를 아껴주신 여러분께\n진심으로 감사의 말씀을 전합니다.`}</Content>
          <br />
          <Content>아쉽지만 커뮤니티 서비스는</Content>
          <Content>
            <span>2024년 8월 30일부로 잠정 중단</span>될 예정이에요.
          </Content>
          <br />
          <Content>{`더 좋은 서비스를 위해 잠시 쉬어가는 것이니\n여러분들의 많은 양해 부탁드립니다.`}</Content>
          <br />
          <Content>재정비 후 개편된 커뮤니티 서비스로 찾아 뵙겠습니다.</Content>
          <br />
          <Content>감사합니다</Content>
        </MobileContentScrollWrapper>
        <CheckButton onClick={() => setTodayInvisible((prev) => !prev)}>
          <Check checked={todayInvisible}>
            {todayInvisible ? (
              <ValidationCheck size={20} fill={theme.color.white} />
            ) : (
              <div />
            )}
          </Check>
          <span>오늘 하루 보이지 않기</span>
        </CheckButton>
        <CloseButton onClick={() => onCloseWithTodayInvisible(todayInvisible)}>
          닫기
        </CloseButton>
      </MobileWrapper>
    );
  }
  return (
    <BottomSheet
      isOpened={true}
      index={200}
      onClickOverlay={() => onCloseWithTodayInvisible(false)}
    >
      <Wrapper>
        <OutOfServiceSvg />
        <Header>커뮤니티 서비스 중단 안내</Header>
        <ContentWrapper>
          <Content>{`안녕하세요. 닥터나우 입니다.\n먼저 커뮤니티 서비스를 아껴주신 여러분께\n진심으로 감사의 말씀을 전합니다.`}</Content>
          <br />
          <Content>아쉽지만 커뮤니티 서비스는</Content>
          <Content>
            <span>2024년 8월 30일부로 잠정 중단</span>될 예정이에요.
          </Content>
          <br />
          <Content>{`더 좋은 서비스를 위해 잠시 쉬어가는 것이니\n여러분들의 많은 양해 부탁드립니다.`}</Content>
          <br />
          <Content>재정비 후 개편된 커뮤니티 서비스로 찾아 뵙겠습니다.</Content>
          <br />
          <Content>감사합니다</Content>
        </ContentWrapper>
        <CheckButton onClick={() => setTodayInvisible((prev) => !prev)}>
          <Check checked={todayInvisible}>
            {todayInvisible ? (
              <ValidationCheck size={20} fill={theme.color.primary600} />
            ) : (
              <div />
            )}
          </Check>
          <span>오늘 하루 보이지 않기</span>
        </CheckButton>
        <CloseButton onClick={() => onCloseWithTodayInvisible(todayInvisible)}>
          닫기
        </CloseButton>
      </Wrapper>
    </BottomSheet>
  );
};

export default CommunityCloseNotice;

const MobileWrapper = styled.div`
  width: calc(100% - 40px);
  padding: 24px;
  border-radius: 16px;
  ${({ theme }) => css`
    background-color: ${theme.color.white};
  `}
`;

const Wrapper = styled.div`
  max-width: 560px;
  width: 100%;
  padding: 24px 20px 32px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const Header = styled.div`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
  `}
  margin: 8px 0;
`;

const MobileHeader = styled(Header)`
  ${({ theme }) => css`
    ${theme.typography.UI18SB}
    color: ${theme.color.gray900};
    margin-bottom: 12px;
  `}
`;

const Content = styled.div`
  ${({ theme }) => css`
    ${theme.typography.UI15R}
    color: ${theme.color.gray600};
    white-space: pre-line;
    & > span {
      ${theme.typography.UI15B}
    }
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const MobileContentScrollWrapper = styled.div`
  max-height: 270px;
  height: 100%;
  overflow-y: scroll;
  border-radius: 8px;
  padding: 16px;
  ${({ theme }) => css`
    ${theme.typography.UI18SB}
    color: ${theme.color.gray900};
    border: 1px solid ${theme.color.gray100};
  `}
  margin-bottom: 24px;
`;

const Check = styled.div<{ checked: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border: 2px solid ${theme.color.gray100};
  border-radius: 4px;
  ${({ checked }) =>
    checked &&
    css`
      background-color: ${theme.color.primary600};
      border-color: ${theme.color.primary600};
    `}
  transition: background-color 0.3s, border-color 0.3s;
`;

const CheckButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 12px;
  ${({ theme }) => css`
    & > span {
      flex: 1;
      ${theme.typography.UI15R}
      color: ${theme.color.gray600};
    }
  `}
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  width: 100%;
  height: 56px;
  padding: 0 24px;
  border-radius: 12px;
  ${({ theme }) => css`
    ${theme.typography.UI17SB}
    color: ${theme.color.gray600};
    background-color: ${theme.color.gray50};
  `}
`;

const OutOfServiceSvg = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
    >
      <path
        d="M0.369847 23.7613C1.03018 19.8102 4.60009 15.324 6.30251 13.5748C13.2773 8.43012 22.2433 11.4655 25.287 12.9575C25.7685 13.5405 26.2053 14.1099 28.4339 16.8674C31.2197 20.3144 31.9419 25.8192 31.9935 26.5909C32.0451 27.3626 31.7872 28.2886 31.2713 28.8545C30.8586 29.3073 29.7924 29.7977 29.3109 29.9864L24.668 31.8385C24.3791 31.9208 24.1005 31.6327 23.9973 31.4783L21.6243 26.7452V27.8256H10.2232V26.7452C9.58697 28.1171 8.24223 30.9845 7.95334 31.4783C7.66444 31.9722 7.2139 31.8556 7.02475 31.7356L3.87786 30.4494C-0.146036 28.8545 -0.455567 28.7002 0.369847 23.7613Z"
        fill="#EDEFF0"
      />
      <path
        d="M24.598 19.4463C24.3382 16.0393 22.514 16 22.4367 16C22.3185 16 22.2222 16.0961 22.2222 16.2139C22.2222 16.3318 22.3185 16.4279 22.4367 16.4279C22.4513 16.4279 23.9296 16.4628 24.1675 19.4477C23.5195 19.5511 23.0234 20.1114 23.0234 20.7852V23.3961C23.0234 23.514 23.1197 23.61 23.2379 23.61C23.3561 23.61 23.4524 23.514 23.4524 23.3961V20.7852C23.4524 20.2729 23.8698 19.8567 24.3835 19.8567C24.8972 19.8567 25.3145 20.2729 25.3145 20.7852V23.3961C25.3145 23.514 25.4109 23.61 25.5291 23.61C25.6473 23.61 25.7436 23.514 25.7436 23.3961V20.7852C25.7436 20.1099 25.246 19.5496 24.5965 19.4463H24.598Z"
        fill="#303030"
      />
      <path
        d="M24.0624 26.6547C23.5487 26.6547 23.1313 26.2384 23.1313 25.7262V23.5053C23.1313 23.4456 23.1795 23.399 23.2379 23.399C23.2963 23.399 23.3444 23.4471 23.3444 23.5053V25.7262C23.3444 26.1206 23.6655 26.4407 24.061 26.4407C24.1208 26.4407 24.1675 26.4888 24.1675 26.547C24.1675 26.6052 24.1193 26.6532 24.061 26.6532L24.0624 26.6547Z"
        fill="#A5A5A5"
      />
      <path
        d="M24.0418 26.2961L23.9046 26.2933C23.769 26.2904 23.6569 26.3977 23.654 26.5328C23.6512 26.668 23.7588 26.7798 23.8943 26.7827L24.0316 26.7855C24.1671 26.7884 24.2792 26.6811 24.2821 26.546C24.2849 26.4108 24.1774 26.299 24.0418 26.2961Z"
        fill="#CBCBCB"
      />
      <path
        d="M24.7076 26.6546C24.6478 26.6546 24.6011 26.6066 24.6011 26.5484C24.6011 26.4902 24.6492 26.4422 24.7076 26.4422C25.1031 26.4422 25.4242 26.122 25.4242 25.7276V23.5067C25.4242 23.4471 25.4723 23.4005 25.5307 23.4005C25.5891 23.4005 25.6372 23.4485 25.6372 23.5067V25.7276C25.6372 26.2399 25.2198 26.6561 24.7062 26.6561L24.7076 26.6546Z"
        fill="#A5A5A5"
      />
      <path
        d="M24.8283 26.7857L24.9656 26.7828C25.1011 26.78 25.2086 26.6682 25.2058 26.533C25.203 26.3979 25.0908 26.2906 24.9553 26.2934L24.818 26.2963C24.6825 26.2991 24.5749 26.411 24.5778 26.5461C24.5806 26.6813 24.6928 26.7885 24.8283 26.7857Z"
        fill="#CBCBCB"
      />
      <path
        d="M10.2146 27.8843V17.0804H21.7704V27.8843H10.2146Z"
        fill="#1B64B1"
      />
      <ellipse
        cx="8.04768"
        cy="12.5016"
        rx="2.57942"
        ry="2.62379"
        fill="#FEC7A8"
      />
      <ellipse
        cx="23.9368"
        cy="12.5016"
        rx="2.57942"
        ry="2.62379"
        fill="#FEC7A8"
      />
      <ellipse
        cx="15.9403"
        cy="10.3408"
        rx="9.54385"
        ry="9.62057"
        fill="#FFDED0"
      />
      <path
        d="M19.7893 0.806638C17.1928 -0.480052 15.7864 0.0560692 15.4078 0.484966C13.8932 -0.501494 10.9542 1.25341 9.67403 2.25416C4.22153 7.05781 6.18272 13.1109 7.84155 15.4341C11.1736 14.8336 12.7573 12.369 13.1359 11.3146C16.468 12.9444 21.9903 14.4528 24.2982 14.9711C25.2503 14.0704 25.6673 11.2074 25.7935 9.92069C25.8476 5.63172 23.0348 2.415 19.7893 0.806638Z"
        fill="#1C1D1F"
      />
      <ellipse
        cx="13.8262"
        cy="17.3894"
        rx="0.722638"
        ry="0.514755"
        fill="#1C1D1F"
      />
      <ellipse
        cx="18.1594"
        cy="17.3894"
        rx="0.722638"
        ry="0.514755"
        fill="#1C1D1F"
      />
      <path
        d="M15.3425 30.3498C15.0548 29.6825 11.7901 27.6735 10.2148 26.7524L8.0481 31.2361L8.61315 31.5489C9.10629 31.8409 9.81176 31.9486 10.1028 31.966C11.3357 32.0008 13.9966 32.039 14.7774 31.9139C15.7534 31.7575 15.702 31.184 15.3425 30.3498Z"
        fill="#FFDED0"
      />
      <path
        d="M16.6426 30.5089C16.9303 29.9075 20.307 27.8912 21.8823 27.0611L23.937 31.3077L23.372 31.5896C22.8788 31.8528 22.1733 31.9499 21.8823 31.9655C20.6494 31.9968 17.9885 32.0313 17.2077 31.9185C16.2317 31.7776 16.2831 31.2607 16.6426 30.5089Z"
        fill="#FFDED0"
      />
    </svg>
  );
};
